import { trpc } from '@agentic-flow-studio/shared/lib/trpc';
import type { JobPlanVerticalKey } from '@agentic-flow-studio/shared/types/job-plan';
import { Badge } from '@agentic-flow-studio/shared/ui/badge';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@agentic-flow-studio/shared/ui/dialog';
import { CheckCircle2, ChevronLeft, ChevronRight, Loader2, Route, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface JobPlanOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenIngest?: () => void;
}

const VERTICAL_OPTIONS: Array<{
  key: JobPlanVerticalKey;
  label: string;
  description: string;
  icon: string;
  starterCount: number;
  starterSkills: string;
}> = [
  {
    key: 'trades',
    label: 'Trades & Maintenance',
    description: 'Contractors, HVAC diagnostics, electrical repairs, plumbing, emergency callouts.',
    icon: '🔧',
    starterCount: 3,
    starterSkills: 'HVAC Specialist, Licensed Electrician, Master Plumber',
  },
  {
    key: 'logistics',
    label: 'Logistics & Couriers',
    description:
      'Parcel distribution, pallet transport, multi-drop regional manifests, container transport.',
    icon: '🚚',
    starterCount: 3,
    starterSkills: 'Heavy Vehicle Driver, Forklift Operator, Dangerous Goods',
  },
  {
    key: 'it-telecom',
    label: 'IT & Telecommunications',
    description: 'Onsite network cabling, server rack installs, hardware swaps, Wi-Fi deployment.',
    icon: '💻',
    starterCount: 3,
    starterSkills: 'Structured Cabling, Network Engineer, Workstation Tech',
  },
  {
    key: 'healthcare',
    label: 'Healthcare & Mobile Care',
    description: 'Home nursing visits, patient care assessments, mobile phlebotomy, mobility aids.',
    icon: '🩺',
    starterCount: 3,
    starterSkills: 'Registered Nurse, Occupational Therapist, Mobile Phlebotomist',
  },
  {
    key: 'facilities',
    label: 'Facilities & Cleaning',
    description:
      'Commercial deep cleaning, quarterly pest control, exterior grounds & lawn maintenance.',
    icon: '🧹',
    starterCount: 3,
    starterSkills: 'Deep Cleaner, Pest Control Tech, Grounds Maintenance',
  },
  {
    key: 'general',
    label: 'General Field Service',
    description:
      'Universal field-service operations, equipment servicing, inspections, and customer repairs.',
    icon: '📋',
    starterCount: 1,
    starterSkills: 'Field Service Technician',
  },
];

export function JobPlanOnboardingModal({
  isOpen,
  onClose,
  onOpenIngest,
}: JobPlanOnboardingModalProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedVertical, setSelectedVertical] = useState<JobPlanVerticalKey>('trades');
  const [customRules, setCustomRules] = useState<string>('');
  const [seedSkills, setSeedSkills] = useState<boolean>(true);

  const utils = trpc.useUtils();

  const completeOnboardingMutation = trpc.jobPlan.completeOnboarding.useMutation({
    onSuccess: (res) => {
      toast.success(
        `Setup complete! ${res.seededSkillCount > 0 ? `Created ${res.seededSkillCount} starter skills.` : ''}`
      );
      utils.jobPlan.getSettings.invalidate();
      utils.jobPlan.skills.list.invalidate();
      setStep(3);
    },
    onError: (err) => toast.error(err.message),
  });

  const handleFinishOnboarding = () => {
    completeOnboardingMutation.mutate({
      verticalKey: selectedVertical,
      customRules: customRules.trim() || undefined,
      seedStarterSkills: seedSkills,
    });
  };

  const currentVerticalMeta =
    VERTICAL_OPTIONS.find((v) => v.key === selectedVertical) || VERTICAL_OPTIONS[0];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="p-5 border-b border-border/40 bg-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                <Sparkles className="h-4 w-4" />
              </div>
              <div>
                <DialogTitle className="text-base font-bold">
                  {step === 1 && 'Welcome to Job Plan — Select Your Vertical'}
                  {step === 2 && 'AI Ingestion & Extraction Directives'}
                  {step === 3 && 'All Set! Your Workspace is Ready'}
                </DialogTitle>
                <DialogDescription className="text-xs">
                  {step === 1 &&
                    'Educate the AI engine with your trade terminology and operational scope.'}
                  {step === 2 &&
                    'Customize extraction rules and starter capabilities for your team.'}
                  {step === 3 &&
                    'Start planning or upload your first legacy work order screenshot.'}
                </DialogDescription>
              </div>
            </div>

            <Badge variant="outline" className="text-xs">
              Step {step} of 3
            </Badge>
          </div>
        </DialogHeader>

        {/* Step Body */}
        <div className="p-6">
          {step === 1 && (
            <div className="space-y-4">
              <span className="text-xs font-semibold text-foreground block">
                Choose your primary domain vertical:
              </span>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[360px] overflow-y-auto pr-1">
                {VERTICAL_OPTIONS.map((opt) => {
                  const isSelected = selectedVertical === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setSelectedVertical(opt.key)}
                      className={`p-3 rounded-xl border text-left flex flex-col justify-between gap-2 transition-all ${
                        isSelected
                          ? 'border-primary bg-primary/[0.04] ring-1 ring-primary shadow-xs'
                          : 'border-border/70 bg-card hover:border-border hover:bg-muted/40'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xl">{opt.icon}</span>
                        {isSelected && (
                          <Badge className="bg-primary text-primary-foreground text-[10px] gap-1 px-1.5 py-0">
                            <CheckCircle2 className="h-2.5 w-2.5" />
                            Selected
                          </Badge>
                        )}
                      </div>

                      <div>
                        <span className="font-semibold text-xs text-foreground block">
                          {opt.label}
                        </span>
                        <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                          {opt.description}
                        </p>
                      </div>

                      <div className="pt-2 border-t border-border/40 text-[10px] text-muted-foreground truncate">
                        <span className="font-medium text-foreground/70">Starter skills: </span>
                        <span>{opt.starterSkills}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="p-3 rounded-xl border border-primary/20 bg-primary/[0.02] flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="text-2xl">{currentVerticalMeta.icon}</span>
                  <div>
                    <span className="text-xs font-semibold text-foreground block">
                      {currentVerticalMeta.label}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      Selected vertical • Can be changed later in Settings
                    </span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep(1)}
                  className="h-7 text-xs text-primary"
                >
                  Change
                </Button>
              </div>

              {/* Starter Skills Toggle */}
              <div className="p-3 rounded-xl border border-border bg-card flex items-start gap-3">
                <input
                  type="checkbox"
                  id="seedStarterSkills"
                  checked={seedSkills}
                  onChange={(e) => setSeedSkills(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                />
                <label htmlFor="seedStarterSkills" className="text-xs space-y-0.5 cursor-pointer">
                  <span className="font-semibold text-foreground block">
                    Pre-seed starter qualifications for {currentVerticalMeta.label}
                  </span>
                  <span className="text-[11px] text-muted-foreground block">
                    Automatically adds: {currentVerticalMeta.starterSkills} (equipped with AI
                    keywords).
                  </span>
                </label>
              </div>

              {/* Custom Ingestion Rules */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  <span>Custom AI Ingest Directives (Optional)</span>
                </label>
                <textarea
                  value={customRules}
                  onChange={(e) => setCustomRules(e.target.value)}
                  placeholder="e.g.
• Work order codes always start with WO-
• High priority if customer mentions leak or outage
• Standard appointment window is 60 minutes"
                  rows={4}
                  className="w-full p-2.5 text-xs rounded-xl border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary font-mono resize-none leading-relaxed"
                />
                <span className="text-[10px] text-muted-foreground block">
                  You can edit these rules at any time in Workspace Settings.
                </span>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="flex flex-col items-center justify-center text-center py-4 space-y-4">
              <div className="h-14 w-14 rounded-full bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8" />
              </div>

              <div className="space-y-1 max-w-sm">
                <span className="font-bold text-base text-foreground block">
                  Your Job Plan is Ready!
                </span>
                <p className="text-xs text-muted-foreground">
                  Configured for{' '}
                  <strong className="text-foreground">{currentVerticalMeta.label}</strong>. You can
                  now import work orders or start planning routes on the schedule board.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full pt-2 max-w-md">
                {onOpenIngest && (
                  <Button
                    type="button"
                    onClick={() => {
                      onClose();
                      onOpenIngest();
                    }}
                    className="gap-1.5 bg-primary hover:bg-primary/90 text-xs h-9"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    <span>Upload Screenshot / Ingest</span>
                  </Button>
                )}

                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="gap-1.5 text-xs h-9"
                >
                  <Route className="h-3.5 w-3.5" />
                  <span>Open Schedule Board</span>
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        {step < 3 && (
          <div className="p-4 border-t border-border/40 bg-card flex items-center justify-between">
            {step === 2 ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setStep(1)}
                className="gap-1 text-xs"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                <span>Back</span>
              </Button>
            ) : (
              <div />
            )}

            {step === 1 ? (
              <Button
                type="button"
                size="sm"
                onClick={() => setStep(2)}
                className="gap-1.5 text-xs bg-primary hover:bg-primary/90"
              >
                <span>Continue</span>
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                onClick={handleFinishOnboarding}
                disabled={completeOnboardingMutation.isPending}
                className="gap-1.5 text-xs bg-primary hover:bg-primary/90"
              >
                {completeOnboardingMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                )}
                <span>Complete Setup</span>
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
